import type { ForcedMovementResolvedCompileInput, ForcedMovementResolvedProvider, ForcedMovementDefenseCompileInput,
    ForcedMovementDefenseCompileResult, PresenceDefenseLayer } from '../types';
import { collectForcedMovementProviderSources } from './forced-movement-provider-sources';
import { compileForcedMovementDefenseSlots } from './forced-movement-defense-compile';

const LAYERS: readonly PresenceDefenseLayer[] = ['force_move_resistance', 'immune_to_forced_movement'];

/** Compose the PHP finalizer over one resolved provider graph, without applying its write plan. */
export function compileResolvedForcedMovementDefenses(input: ForcedMovementResolvedCompileInput): ForcedMovementDefenseCompileResult {
    const { itemId, itemName, itemType } = input.host;
    if (!input.isEncounterCreature && itemType !== 'creature' && itemType !== 'player_character') {
        return { processed: false, slots: {}, removedRootLayers: [] };
    }
    const layers = {} as Record<PresenceDefenseLayer, ForcedMovementDefenseCompileInput['layers'][PresenceDefenseLayer]>;
    for (const layerName of LAYERS) {
        const provider = (node: ForcedMovementResolvedProvider) => ({ itemId: node.itemId, itemName: node.itemName,
            itemType: node.itemType, presence: node.presence[layerName] });
        const sources = collectForcedMovementProviderSources({ layerName, subtypeKinds: input.subtypeKinds,
            host: provider(input.host), carriers: input.carriers.map(node => node === null ? null : provider(node)),
            generalSources: input.generalSources[layerName],
            statuses: input.statuses.map(status => ({ ...status, entry: provider(status.entry),
                definition: status.definition === null ? null : provider(status.definition) })),
            feats: input.feats.map(feat => ({ feat: provider(feat.feat), granted: feat.granted.map(provider) })),
        });
        layers[layerName] = { sources, hasLayer: input.fallbackHasLayer[layerName] };
    }
    return compileForcedMovementDefenseSlots({ itemId, itemName, itemType, isEncounterCreature: input.isEncounterCreature, layers });
}
